import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  CheckCircle2,
  FileCheck2,
  Loader2,
  Mail,
  MessageSquare,
  Sparkles,
  Upload,
} from "lucide-react";
import { toast } from "sonner";

import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";

export default function TestSubmittedPage() {
  const { leadId } = useParams();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);

  useEffect(() => {
    if (leadId) localStorage.removeItem(`medix_quiz_${leadId}`);
  }, [leadId]);

  const onPick = (f) => {
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error("File must be under 10MB");
      return;
    }
    const allowed = ["image/jpeg", "image/png", "image/webp", "application/pdf"];
    if (!allowed.includes(f.type)) {
      toast.error("Only JPEG, PNG, WEBP or PDF are allowed");
      return;
    }
    setFile(f);
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Please select your NEET scorecard file");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/leads/${leadId}/scorecard`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploaded(true);
      toast.success("Scorecard uploaded. You're in line for a higher slab!");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-slate-200">
        <div className="mx-auto h-16 max-w-3xl px-4 sm:px-6">
          <div className="flex h-full items-center justify-between">
            <Link
              to="/"
              className="font-display text-base font-bold tracking-tight text-[#0A2540]"
              data-testid="submitted-home-link"
            >
              MedAdmits
            </Link>
            <div className="text-xs uppercase tracking-[0.22em] text-slate-500">
              Step 2 of 2 &middot; Submitted
            </div>
          </div>
        </div>
        <div className="h-1 w-full bg-[#166534]" />
      </header>

      <main
        className="mx-auto max-w-2xl px-4 py-16 sm:px-6 sm:py-24"
        data-testid="submitted-screen"
      >
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#166534]/10">
          <CheckCircle2 className="h-8 w-8 text-[#166534]" strokeWidth={2.2} />
        </div>
        <div className="mt-6 text-xs uppercase tracking-[0.22em] text-[#166534]">
          Test Submitted
        </div>
        <h1 className="font-display mt-3 text-3xl font-bold leading-tight tracking-tight text-[#0A2540] sm:text-4xl">
          Brilliant. Your scholarship test is locked in.
        </h1>
        <p className="mt-4 text-slate-600">
          Our counsellors are reviewing your answers along with your NEET
          profile.{" "}
          <strong className="text-[#0A2540]">
            Results &amp; scholarship eligibility will be released within 48
            hours via SMS and Email.
          </strong>{" "}
          No need to refresh this page.
        </p>

        {/* SCORECARD UPLOAD — UNLOCK HIGHER SLAB */}
        <div
          className="mt-10 rounded-2xl border border-[#C9A961]/40 bg-gradient-to-br from-[#FFF8E7] via-white to-white p-6 sm:p-7"
          data-testid="scorecard-unlock-card"
        >
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.22em] text-[#8B6B1F]">
            <Sparkles className="h-3.5 w-3.5" /> Boost your scholarship
          </div>
          <h2 className="font-display mt-2 text-xl font-bold leading-tight text-[#0A2540] sm:text-2xl">
            🎓 Unlock your scholarship slab letter
          </h2>
          <p className="mt-2 text-sm text-slate-600">
            Upload your NEET scorecard to verify and{" "}
            <strong className="text-[#0A2540]">
              increase your chances to get the higher scholarship
            </strong>
            . Verified students get priority slab review and a written
            scholarship letter from our admissions desk.
          </p>

          {uploaded ? (
            <div
              className="mt-5 flex items-center gap-3 rounded-xl border border-[#166534]/30 bg-[#166534]/5 p-4"
              data-testid="scorecard-uploaded"
            >
              <FileCheck2 className="h-6 w-6 text-[#166534]" />
              <div>
                <div className="text-sm font-semibold text-[#166534]">
                  Scorecard received
                </div>
                <div className="text-xs text-slate-500">
                  We&rsquo;ll email your verified scholarship slab letter within
                  48 hours.
                </div>
              </div>
            </div>
          ) : (
            <>
              <div
                onClick={() => fileRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => {
                  e.preventDefault();
                  onPick(e.dataTransfer.files?.[0]);
                }}
                className="mt-5 cursor-pointer rounded-xl border-2 border-dashed border-[#C9A961]/50 bg-white/70 p-6 text-center transition-colors hover:border-[#0A2540]"
                data-testid="scorecard-dropzone"
              >
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,application/pdf"
                  className="hidden"
                  onChange={(e) => onPick(e.target.files?.[0])}
                  data-testid="scorecard-input"
                />
                {file ? (
                  <div className="flex flex-col items-center gap-2">
                    <FileCheck2 className="h-7 w-7 text-[#166534]" />
                    <div className="text-sm font-medium text-[#0A2540]">
                      {file.name}
                    </div>
                    <div className="text-xs text-slate-500">
                      {(file.size / 1024).toFixed(1)} KB
                    </div>
                    <button
                      type="button"
                      className="text-xs text-slate-500 underline"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFile(null);
                      }}
                    >
                      Choose a different file
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <Upload
                      className="h-7 w-7 text-[#0A2540]"
                      strokeWidth={1.6}
                    />
                    <div className="text-sm font-medium text-[#0A2540]">
                      Click or drop your NEET scorecard here
                    </div>
                    <div className="text-xs text-slate-500">
                      JPG, PNG, WEBP or PDF &middot; up to 10MB
                    </div>
                  </div>
                )}
              </div>

              <Button
                onClick={handleUpload}
                disabled={uploading || !file}
                className="mt-4 h-11 w-full bg-[#0A2540] text-sm font-semibold text-white hover:bg-[#1E3A5F] disabled:opacity-60"
                data-testid="scorecard-submit"
              >
                {uploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Uploading…
                  </>
                ) : (
                  <>Unlock my scholarship slab letter</>
                )}
              </Button>
              <p className="mt-2 text-center text-[11px] text-slate-500">
                Optional — but verified students typically move{" "}
                <strong>one slab higher</strong> in scholarship review.
              </p>
            </>
          )}
        </div>

        <div className="mt-10 grid gap-px overflow-hidden rounded-xl border border-slate-200 bg-slate-200 sm:grid-cols-3">
          <div className="bg-white p-5">
            <Mail className="h-5 w-5 text-[#0A2540]" />
            <div className="mt-3 text-sm font-semibold text-[#0A2540]">
              Email
            </div>
            <p className="mt-1 text-xs text-slate-500">
              Detailed scorecard + university shortlist.
            </p>
          </div>
          <div className="bg-white p-5">
            <MessageSquare className="h-5 w-5 text-[#0A2540]" />
            <div className="mt-3 text-sm font-semibold text-[#0A2540]">SMS</div>
            <p className="mt-1 text-xs text-slate-500">
              Quick summary &amp; counselling slot link.
            </p>
          </div>
          <div className="bg-white p-5">
            <Sparkles className="h-5 w-5 text-[#0A2540]" />
            <div className="mt-3 text-sm font-semibold text-[#0A2540]">Call</div>
            <p className="mt-1 text-xs text-slate-500">
              Senior counsellor will personally walk you through options.
            </p>
          </div>
        </div>

        <div className="mt-10 rounded-xl border border-slate-200 bg-[#F8F9FA] p-5">
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500">
            Your reference ID
          </div>
          <div className="font-mono-clock mt-1 text-sm text-[#0A2540]">
            {leadId}
          </div>
        </div>

        <Link
          to="/"
          className="mt-10 inline-flex h-11 items-center justify-center rounded-md border border-slate-300 bg-white px-6 text-sm font-medium text-[#0A2540] hover:border-[#0A2540]"
          data-testid="submitted-back-home"
        >
          Back to home
        </Link>
      </main>
    </div>
  );
}
